<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'
import html2pdf from 'html2pdf.js'

export default {
  page: {
    title: 'Tenable Assets',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader,
    apexchart: VueApexCharts
  },
  data () {
    return {
      updated: '',
      assets: [],
      filteredAssets: [],
      targetGroups: [], // Store target groups for label extraction
      title: 'Tenable Assets',
      loading: true,
      // Chart data and options for average score by OS family
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false
          },
          dynamicHeight: true
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            dataLabels: {
              position: 'top'
            },
            barHeight: '70%'
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 30,
          style: {
            fontSize: '12px',
            colors: ['#fff']
          },
          formatter: function(val) {
            return Math.round(val)
          }
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              colors: [],
              fontSize: '12px'
            }
          },
          title: {
            text: 'Average Exposure Score',
            style: {
              fontSize: '14px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Operating System',
            style: {
              fontSize: '14px'
            }
          },
          labels: {
            maxWidth: 1000,
            style: {
              fontSize: '12px'
            },
            formatter: function(value) {
              return value;
            }
          }
        },
        colors: [],
        tooltip: {
          y: {
            formatter: function(val) {
              return Math.round(val) + ' (avg exposure score)'
            }
          }
        }
      },
      chartSeries: [{
        name: 'Average Exposure Score',
        data: []
      }],
      // Chart data and options for exact OS versions
      exactOSChartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false
          },
          dynamicHeight: true
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            dataLabels: {
              position: 'top'
            },
            barHeight: '70%'
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 30,
          style: {
            fontSize: '12px',
            colors: ['#fff']
          },
          formatter: function(val) {
            return Math.round(val)
          }
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              colors: [],
              fontSize: '12px'
            }
          },
          title: {
            text: 'Total Exposure Score',
            style: {
              fontSize: '14px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Exact Operating System',
            style: {
              fontSize: '14px'
            }
          },
          labels: {
            maxWidth: 1000,
            style: {
              fontSize: '12px'
            },
            formatter: function(value) {
              return value;
            }
          }
        },
        colors: [],
        tooltip: {
          y: {
            formatter: function(val) {
              return Math.round(val) + ' (total exposure)'
            }
          }
        }
      },
      exactOSChartSeries: [{
        name: 'Total Exposure Score',
        data: []
      }],
      // Chart data and options for maximum exposure score by OS
      maxScoreChartOptions: {
        chart: {
          type: 'bar',
          height: 600,
          toolbar: {
            show: false
          },
          dynamicHeight: true
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            dataLabels: {
              position: 'top'
            },
            barHeight: '70%'
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 30,
          style: {
            fontSize: '12px',
            colors: ['#fff']
          },
          formatter: function(val) {
            return Math.round(val)
          }
        },
        legend: {
          show: false
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              colors: [],
              fontSize: '12px'
            }
          },
          title: {
            text: 'Maximum Exposure Score',
            style: {
              fontSize: '14px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Operating System',
            style: {
              fontSize: '14px'
            }
          },
          labels: {
            maxWidth: 1000,
            style: {
              fontSize: '12px'
            },
            formatter: function(value) {
              return value;
            }
          }
        },
        colors: [],
        tooltip: {
          y: {
            formatter: function(val) {
              return Math.round(val) + ' (max exposure score)'
            }
          }
        }
      },
      maxScoreChartSeries: [{
        name: 'Maximum Exposure Score',
        data: []
      }],
      // Search filters - enhanced to match vulnerabilities_assets.vue
      filters: {
        asset: '' // Changed from 'search' to 'asset' to match vulnerabilities_assets.vue
      },
      // Sorting
      sortKey: 'exposure_score',
      sortDirection: 'desc',
      // Selected exposure score levels for multi-select
      selectedExposureScores: [],
      // Selected labels for multi-select filtering
      selectedLabels: [],
      // Exposure score options for checkboxes
      exposureScoreOptions: [
        { value: 'Critical', text: 'Critical', range: [800, 1000] },
        { value: 'High', text: 'High', range: [600, 799] },
        { value: 'Medium', text: 'Medium', range: [400, 599] },
        { value: 'Low', text: 'Low', range: [200, 399] },
        { value: 'Info', text: 'Info', range: [0, 199] }
      ],
      // Date range selection dropdown
      selectedDateRange: '7',
      dateRangeOptions: [
        { value: '1', text: '1 Day' },
        { value: '7', text: '7 Days' },
        { value: '15', text: '15 Days' },
        { value: '30', text: '30 Days' },
        { value: '45', text: '45 Days' },
        { value: '90', text: '90 Days' },
        { value: '180', text: '180 Days' },
        { value: '365', text: '1 Year' }
      ],
      items: [
        {
          text: 'Home',
          href: '/'
        },
        {
          text: 'Tenable',
          href: '/tenable'
        },
        {
          text: 'Assets',
          active: true
        }
      ],
      // Column headers for the table
      // Column headers for the table with sortable property
      columns: [
        {
          key: 'netbios_name',
          label: 'Name',
          sortable: true
        },
        {
          key: 'fqdn',
          label: 'FQDN',
          sortable: true
        },
        {
          key: 'ipv4',
          label: 'IPv4',
          sortable: true
        },
        {
          key: 'labels',
          label: 'Labels',
          sortable: true
        },
        {
          key: 'last_seen',
          label: 'Last Seen',
          sortable: true
        },
        {
          key: 'operating_system',
          label: 'Operating System',
          sortable: true
        },
        {
          key: 'exposure_score',
          label: 'Exposure Score',
          sortable: true
        }
      ],
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      pdfConfig: {
        margin: 0.2,
        filename: 'Tenable Assets Report - Corona.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, dpi: 96, letterRendering: true, width: 1280 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
        pagebreak: { mode: 'css', before: '#page2el' },
        _pageBreak: { mode: 'avoid-all', before: '.break-page', after: '.break-page' }
      }
    }
  },
  computed: {
    /**
     * Computed property to extract all unique labels from target groups
     * This provides the complete list of available labels for the filter dropdown
     * 
     * @returns {Array} Array of unique label names, sorted alphabetically
     * 
     * How it works:
     * 1. Checks if target groups data is available and is an array
     * 2. Maps each target group to its name property
     * 3. Filters out empty/null names and the 'Default' group
     * 4. Creates a unique set and sorts alphabetically
     * 5. Returns the final array for use in the label filter UI
     */
    allLabels () {
      // Return empty array if no target groups data available
      if (!this.targetGroups || !Array.isArray(this.targetGroups)) {
        console.log('No target groups available for labels:', this.targetGroups)
        return []
      }

      console.log('Processing target groups for labels:', this.targetGroups.length, 'groups')
      
      // Extract names from target groups, filtering out invalid entries
      const labels = this.targetGroups.map(group => {
        console.log('Processing target group:', group.name)
        return group.name
      }).filter(name => name && name !== 'Default') // Exclude 'Default' group
      
      // Create unique set and sort alphabetically
      const uniqueLabels = [...new Set(labels)].sort()
      console.log('Available labels for filtering:', uniqueLabels)
      
      return uniqueLabels
    }
  },
  mounted () {
    // Select all exposure scores by default
    // this.selectedExposureScores = this.exposureScoreOptions.map(option => option.value)
    this.selectedExposureScores = this.exposureScoreOptions.filter(option => option.value === 'Critical').map(option => option.value)
    
    // Default date range is already set to 7 days
    this.loadTargetGroups()
    this.loadData()
  },
  
  watch: {
    // Watch for changes in filters and update filtered data
    filters: {
      handler () {
        this.applyFilters()
      },
      deep: true
    },
    // Watch for changes in selected labels and apply filters
    selectedLabels: {
      handler () {
        this.applyFilters()
      }
    },
    // Update filtered data when raw data changes
    assets: {
      handler() {
        this.applyFilters()
      },
      immediate: true
    },
    // Reload data when exposure score selection changes
    selectedExposureScores: {
      handler() {
        this.applyFilters()
      },
      deep: true
    },
    // Reload data when date range selection changes
    selectedDateRange: {
      handler () {
        this.loadData()
      }
    }
  },
  methods: {
    /**
     * Load target groups data for label extraction
     * This method fetches the target groups which contain IP-to-label mappings
     * used for filtering assets by labels
     */
    loadTargetGroups () {
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      const urlTG = `${baseURL}/tenable/target-groups`
      
      axios.get(urlTG)
        .then((response) => {
          console.log('Target groups loaded:', response)
          if (response.data) {
            this.targetGroups = response.data.target_groups || []
            // Set default selection to all labels on first load (but don't filter when all are selected)
            if (this.selectedLabels.length === 0) {
              this.$nextTick(() => {
                this.selectedLabels = [...this.allLabels]
              })
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching target groups:', error)
        })
    },
    
    /**
     * Load assets data with enhanced filtering support
     * This method now supports date range filtering using start/end dates
     * instead of a predefined dropdown selection
     */
    loadData () {
      let that = this
      let baseURL = that.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      
      // Build query parameters for date range
      // Note: The Tenable /assets endpoint doesn't support date filtering via API parameters
      // We'll fetch all assets and filter client-side based on last_seen
      const params = {}
      // Remove API-level date filtering since it's not supported
      // We'll filter on the client side after receiving the data
      
      // Convert params to URL query string
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&')
      
      const url = `${baseURL}/tenable/assets${queryString ? '?' + queryString : ''}`

      that.loading = true

      // Debug logging for date filtering
      console.log('Loading assets with URL:', url)
      console.log('Query parameters:', params)
      console.log('Selected date range:', this.selectedDateRange, 'days')

      axios.get(url)
        .then((response) => {
          console.log('API Response:', response.data)
          console.log('Response length:', response.data ? response.data.length : 'No data')
          
          // Check if response.data exists and has the expected format
          let assetsData = []
          
          if (response.data && Array.isArray(response.data)) {
            assetsData = response.data
          } else if (response.data && response.data.assets && Array.isArray(response.data.assets)) {
            // Handle case where API returns data in a nested 'assets' property
            assetsData = response.data.assets
          } else {
            console.error('Unexpected API response format:', response.data)
            assetsData = []
          }
          
          // Filter assets by last_seen date (client-side filtering)
          const filteredByDate = that.filterAssetsByDateRange(assetsData)
          console.log('Assets after date filtering:', filteredByDate.length, 'out of', assetsData.length)
          
          // Normalize exposure scores for filtered assets
          that.assets = filteredByDate.map(asset => {
            // Create a new object to avoid mutating the original
            const normalizedAsset = { ...asset }
            
            // Set exposure_score to 0 if null, undefined, non-numeric, or negative
            if (normalizedAsset.exposure_score === undefined || 
                normalizedAsset.exposure_score === null || 
                isNaN(parseFloat(normalizedAsset.exposure_score)) || 
                parseFloat(normalizedAsset.exposure_score) < 0) {
              normalizedAsset.exposure_score = 0
            } else {
              // Ensure it's a number (not a string)
              normalizedAsset.exposure_score = parseFloat(normalizedAsset.exposure_score)
            }
            
            return normalizedAsset
          })
          const now = new Date()
          that.updated = now.toLocaleString()
          that.loading = false
        })
        .catch(function (error) {
          console.error('Error fetching asset data:', error)
          that.assets = []
          that.loading = false
        })
    },
    // Format the date from timestamp
    formatDate (timestamp) {
      if (!timestamp) return 'N/A'

      try {
        const parsedDate = Date.parse(timestamp)
        const date = new Date(parsedDate)

        // Check if date is valid
        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString()
      } catch (e) {
        return 'N/A'
      }
    },
    // Legacy method - kept for backward compatibility
    getRiskScoreClass (score) {
      if (!score && score !== 0) return 'badge bg-secondary'
      
      if (score >= 7) {
        return 'badge bg-danger'
      } else if (score >= 4) {
        return 'badge bg-warning'
      } else {
        return 'badge bg-success'
      }
    },
    
    // Get exposure score class for badge styling (0-1000 scale)
    getExposureScoreClass (score) {
      if (score === undefined || score === null) return 'badge bg-light text-dark'
      
      // Convert to number if it's a string
      const numScore = typeof score === 'string' ? parseFloat(score) : score
      
      // Scale: 0-200 (Info), 201-400 (Low), 401-600 (Medium), 601-800 (High), 801-1000 (Critical)
      if (numScore >= 800) {
        return 'badge bg-danger'       // Critical (red)
      } else if (numScore >= 600) {
        return 'badge bg-warning'      // High (yellow/orange)
      } else if (numScore >= 400) {
        return 'badge bg-info'         // Medium (blue)
      } else if (numScore >= 200) {
        return 'badge bg-success'      // Low (green)
      } else {
        return 'badge bg-light text-dark'    // Info (light with dark text for better visibility)
      }
    },
    
    // Get background color for exposure score
    getExposureScoreBackgroundColor(score) {
      if (score === undefined || score === null) return '#6c757d' // secondary
      
      // Convert to number if it's a string
      const numScore = typeof score === 'string' ? parseFloat(score) : score
      
      if (numScore >= 800) {
        return '#dc3545' // danger
      } else if (numScore >= 600) {
        return '#ffc107' // warning
      } else if (numScore >= 400) {
        return '#0dcaf0' // info
      } else if (numScore >= 200) {
        return '#198754' // success
      } else {
        return '#6c757d' // secondary
      }
    },
    
    // Normalize operating system name for chart
    normalizeOSName(osName) {
      if (!osName) return 'Unknown'
      
      // Extract the main OS name without version details
      let normalizedName = osName.trim()
      
      // Handle common OS patterns
      if (normalizedName.toLowerCase().includes('windows')) {
        return 'Windows'
      } else if (normalizedName.toLowerCase().includes('linux')) {
        return 'Linux'
      } else if (normalizedName.toLowerCase().includes('mac') || normalizedName.toLowerCase().includes('os x')) {
        return 'macOS'
      } else if (normalizedName.toLowerCase().includes('android')) {
        return 'Android'
      } else if (normalizedName.toLowerCase().includes('ios')) {
        return 'iOS'
      } else if (normalizedName.toLowerCase().includes('unix')) {
        return 'Unix'
      } else {
        // If it's a long string, truncate it
        return normalizedName.length > 15 ? normalizedName.substring(0, 15) + '...' : normalizedName
      }
    },
    
    // Calculate average exposure score by operating system
    calculateAverageScoreByOS() {
      // Group assets by operating system and calculate average exposure score
      const osScores = {}
      const osCounts = {}
      
      this.filteredAssets.forEach(asset => {
        // Set exposure_score to 0 if null, undefined, non-numeric, or less than zero
        let exposureScore = asset.exposure_score === undefined || asset.exposure_score === null || isNaN(parseFloat(asset.exposure_score)) ? 0 : parseFloat(asset.exposure_score)
        // Ensure exposure score is not negative
        exposureScore = exposureScore < 0 ? 0 : exposureScore
        
        // Handle different OS data formats
        let osList = []
        
        if (Array.isArray(asset.operating_system)) {
          osList = asset.operating_system
        } else if (typeof asset.operating_system === 'string') {
          if (asset.operating_system.includes(',')) {
            osList = asset.operating_system.split(',').map(os => os.trim())
          } else {
            osList = [asset.operating_system]
          }
        }
        
        // Skip if no OS information
        if (osList.length === 0 || !osList[0]) return
        
        // Use only the first OS for simplicity
        const normalizedOS = this.normalizeOSName(osList[0])
        // Use the already validated exposureScore
        if (!osScores[normalizedOS]) {
          osScores[normalizedOS] = 0
          osCounts[normalizedOS] = 0
        }
        osScores[normalizedOS] += exposureScore
        osCounts[normalizedOS]++
      })
      
      // Calculate averages and prepare data for chart
      const result = []
      
      for (const os in osScores) {
        if (osCounts[os] > 0) {
          result.push({
            os: os,
            avgScore: osScores[os] / osCounts[os],
            count: osCounts[os]
          })
        }
      }
      
      // Sort by average score (highest first)
      return result.sort((a, b) => b.avgScore - a.avgScore)
    },
    
    // Render the chart with OS and exposure score data
    renderChart() {
      if (this.filteredAssets.length === 0) return
      
      // Get average scores by OS
      const osScoreData = this.calculateAverageScoreByOS()
      
      // Take top 10 OS by average exposure score
      const topOS = osScoreData.slice(0, 10)
      
      if (topOS.length === 0) return
      
      // Prepare data for ApexCharts
      const categories = topOS.map(item => `${item.os} (${item.count})`)
      const scores = topOS.map(item => item.avgScore)
      const colors = topOS.map(item => this.getExposureScoreBackgroundColor(item.avgScore))
      
      // Update chart options and series
      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: categories
        },
        colors: colors
      }
      
      this.chartSeries = [{
        name: 'Average Exposure Score',
        data: scores
      }]
    },
    
    // Calculate total exposure score by exact operating system
    calculateTotalScoreByExactOS() {
      // Group assets by exact operating system and calculate total exposure score
      const osScores = {}
      const osCounts = {}
      
      this.filteredAssets.forEach(asset => {
        // Set exposure_score to 0 if null, undefined, non-numeric, or less than zero
        let exposureScore = asset.exposure_score === undefined || asset.exposure_score === null || isNaN(parseFloat(asset.exposure_score)) ? 0 : parseFloat(asset.exposure_score)
        // Ensure exposure score is not negative
        exposureScore = exposureScore < 0 ? 0 : exposureScore
        
        // Handle different OS data formats
        let osList = []
        
        if (Array.isArray(asset.operating_system)) {
          osList = asset.operating_system
        } else if (typeof asset.operating_system === 'string') {
          if (asset.operating_system.includes(',')) {
            osList = asset.operating_system.split(',').map(os => os.trim())
          } else {
            osList = [asset.operating_system]
          }
        }
        
        // Skip if no OS information
        if (osList.length === 0 || !osList[0]) return
        
        // Process each OS separately to maintain exact OS names
        osList.forEach(os => {
          if (!os || os === 'N/A') return
          
          const exactOS = os.trim()
          // Use the already validated exposureScore
          if (!osScores[exactOS]) {
            osScores[exactOS] = 0
            osCounts[exactOS] = 0
          }
          osScores[exactOS] += exposureScore
          osCounts[exactOS]++
        })
      })
      
      // Prepare data for chart
      const result = []
      
      for (const os in osScores) {
        if (osCounts[os] > 0) {
          result.push({
            os: os,
            totalScore: osScores[os],
            count: osCounts[os],
            avgScore: osScores[os] / osCounts[os]
          })
        }
      }
      
      // Sort by total score (highest first)
      return result.sort((a, b) => b.totalScore - a.totalScore)
    },
    
    // Calculate maximum exposure score by operating system
    calculateMaxScoreByOS() {
      // Track maximum exposure score for each operating system
      const osMaxScores = {}
      const osCounts = {}
      
      this.filteredAssets.forEach(asset => {
        // Set exposure_score to 0 if null, undefined, non-numeric, or less than zero
        let exposureScore = asset.exposure_score === undefined || asset.exposure_score === null || isNaN(parseFloat(asset.exposure_score)) ? 0 : parseFloat(asset.exposure_score)
        // Ensure exposure score is not negative
        exposureScore = exposureScore < 0 ? 0 : exposureScore
        
        // Handle different OS data formats
        let osList = []
        
        if (Array.isArray(asset.operating_system)) {
          osList = asset.operating_system
        } else if (typeof asset.operating_system === 'string') {
          if (asset.operating_system.includes(',')) {
            osList = asset.operating_system.split(',').map(os => os.trim())
          } else {
            osList = [asset.operating_system]
          }
        }
        
        // Skip if no OS information
        if (osList.length === 0 || !osList[0]) return
        
        // Process each OS separately
        osList.forEach(os => {
          if (!os || os === 'N/A') return
          
          const exactOS = os.trim()
          // Use the already validated exposureScore
          if (!osMaxScores[exactOS]) {
            osMaxScores[exactOS] = exposureScore
            osCounts[exactOS] = 1
          } else {
            // Update max score if this asset has a higher score
            osMaxScores[exactOS] = Math.max(osMaxScores[exactOS], exposureScore)
            osCounts[exactOS]++
          }
        })
      })
      
      // Prepare data for chart
      const result = []
      
      for (const os in osMaxScores) {
        if (osCounts[os] > 0) {
          result.push({
            os: os,
            maxScore: osMaxScores[os],
            count: osCounts[os]
          })
        }
      }
      
      // Sort by maximum score (highest first)
      return result.sort((a, b) => b.maxScore - a.maxScore)
    },
    
    // Render the chart showing maximum exposure score by OS
    renderMaxScoreChart() {
      if (this.filteredAssets.length === 0) return
      
      // Get maximum scores by OS
      const osScoreData = this.calculateMaxScoreByOS()
      
      // Take top 25 OS by maximum exposure score
      const topOS = osScoreData.slice(0, 25)
      
      if (topOS.length === 0) return
      
      // Prepare data for ApexCharts
      const categories = topOS.map(item => `${item.os} (${item.count})`)
      const scores = topOS.map(item => item.maxScore)
      const colors = topOS.map(item => this.getExposureScoreBackgroundColor(item.maxScore))
      
      // Update chart options and series
      this.maxScoreChartOptions = {
        ...this.maxScoreChartOptions,
        xaxis: {
          ...this.maxScoreChartOptions.xaxis,
          categories: categories
        },
        colors: colors
      }
      
      this.maxScoreChartSeries = [{
        name: 'Maximum Exposure Score',
        data: scores
      }]
    },
    
    // Render the chart with exact OS versions and total exposure scores
    renderExactOSChart() {
      if (this.filteredAssets.length === 0) return
      
      // Get total scores by exact OS
      const osScoreData = this.calculateTotalScoreByExactOS()
      
      // Take top 10 OS by total exposure score
      const topOS = osScoreData.slice(0, 10)
      
      if (topOS.length === 0) return
      
      // Prepare data for ApexCharts
      const categories = topOS.map(item => `${item.os} (${item.count})`)
      const scores = topOS.map(item => item.totalScore)
      const colors = topOS.map(item => this.getExposureScoreBackgroundColor(item.avgScore))
      
      // Update chart options and series
      this.exactOSChartOptions = {
        ...this.exactOSChartOptions,
        xaxis: {
          ...this.exactOSChartOptions.xaxis,
          categories: categories
        },
        colors: colors
      }
      
      this.exactOSChartSeries = [{
        name: 'Total Exposure Score',
        data: scores
      }]
    },
    
    /**
     * Extract labels from target groups using IPv4 as members key
     * This method matches the exact implementation from vulnerabilities_assets.vue
     */
    labelsFromTargetGroups (ipv4) {
      let labels = []
      if (!this.targetGroups || !Array.isArray(this.targetGroups)) return labels
      
      this.targetGroups.forEach(group => {
        if (!group.members) return
        const members = group.members.split(', ') || []
        if (members && Array.isArray(members) && members.includes(ipv4)) {
          labels.push(group.name)
        }
      })
      
      return labels
    },

    /**
     * Get the text description for the selected date range
     */
    getSelectedDateRangeText () {
      const option = this.dateRangeOptions.find(opt => opt.value === this.selectedDateRange)
      return option ? option.text : 'Custom Range'
    },

    /**
     * Enhanced filter application method with label filtering
     */
    applyFilters () {
      let result = [...this.assets]
      
      // Enhanced search filter matching vulnerabilities_assets.vue pattern
      if (this.filters.asset) {
        const assetSearch = this.filters.asset.toLowerCase()
        result = result.filter(item => {
          const nameMatch = item.netbios_name && String(item.netbios_name).toLowerCase().includes(assetSearch)
          const fqdnMatch = item.fqdn && String(item.fqdn).toLowerCase().includes(assetSearch)
          const ipMatch = item.ipv4 && String(item.ipv4).toLowerCase().includes(assetSearch)
          const osMatch = item.operating_system && String(item.operating_system).toLowerCase().includes(assetSearch)
          
          return nameMatch || fqdnMatch || ipMatch || osMatch
        })
      }
      
      // Filter by exposure score
      if (this.selectedExposureScores.length > 0 && this.selectedExposureScores.length < this.exposureScoreOptions.length) {
        // Create an array of ranges for the selected exposure scores
        const selectedRanges = this.exposureScoreOptions
          .filter(option => this.selectedExposureScores.includes(option.value))
          .map(option => option.range)
        
        result = result.filter(item => {
          const score = item.exposure_score !== undefined ? Number(item.exposure_score) : -1
          if (score === -1) return false
          
          // Check if the score falls within any of the selected ranges
          return selectedRanges.some(range => score >= range[0] && score <= range[1])
        })
      }
      
      // Filter by selected labels (new functionality)
      if (this.allLabels.length > 0) {
        if (this.selectedLabels.length === 0) {
          // No labels selected = show no assets
          result = []
        } else if (this.selectedLabels.length === this.allLabels.length) {
          // All labels selected = show all assets (no filtering)
          // result remains unchanged
        } else {
          // Some labels selected = filter by selected labels
          result = result.filter(item => {
            let ipList = []
            if (Array.isArray(item.ipv4)) {
              ipList = item.ipv4
            } else if (typeof item.ipv4 === 'string') {
              ipList = [item.ipv4]
            }
            
            if (ipList.length === 0) return false
            
            return this.selectedLabels.some(selectedLabel => {
              return ipList.some(ip => {
                const assetLabels = this.labelsFromTargetGroups(ip)
                return assetLabels.includes(selectedLabel)
              })
            })
          })
        }
      }
      
      // Apply sorting if a sort key is set
      if (this.sortKey) {
        result = this.sortData(result, this.sortKey, this.sortDirection)
      }
      
      this.filteredAssets = result
      
      // Update charts whenever filtered data changes
      this.$nextTick(() => {
        this.renderChart()
        this.renderExactOSChart()
        this.renderMaxScoreChart()
      })
    },
    
    // Sort the data by a specific key and direction
    sortData(data, key, direction) {
      return [...data].sort((a, b) => {
        let aValue = a[key] !== undefined ? a[key] : ''
        let bValue = b[key] !== undefined ? b[key] : ''
        
        // Handle numeric sorting
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return direction === 'asc' ? aValue - bValue : bValue - aValue
        }
        
        // Handle string sorting
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
        
        if (direction === 'asc') {
          return aValue > bValue ? 1 : -1
        } else {
          return aValue < bValue ? 1 : -1
        }
      })
    },
    
    // Toggle sorting when a column header is clicked
    toggleSort(key) {
      // Only allow sorting on sortable columns
      const column = this.columns.find(col => col.key === key)
      if (!column || !column.sortable) return
      
      // If clicking the same column, toggle direction
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        // New column, set as descending for exposure_score, ascending for others
        this.sortKey = key
        this.sortDirection = key === 'exposure_score' ? 'desc' : 'asc'
      }
      
      // Apply the new sorting
      this.applyFilters()
    },
    
    // Get the sort icon for a column
    getSortIcon(key) {
      if (this.sortKey !== key) return 'mdi mdi-sort'
      return this.sortDirection === 'asc' ? 'mdi mdi-sort-ascending' : 'mdi mdi-sort-descending'
    },
    
    // Get exposure score text from numeric value
    getExposureScoreText(score) {
      if (score === undefined || score === null) return 'Unknown'
      
      // Convert to number if it's a string
      const numScore = typeof score === 'string' ? parseFloat(score) : score
      
      if (numScore >= 800) {
        return 'Critical'
      } else if (numScore >= 600) {
        return 'High'
      } else if (numScore >= 400) {
        return 'Medium'
      } else if (numScore >= 200) {
        return 'Low'
      } else {
        return 'Info'
      }
    },
    
    // Get color class for a specific exposure score name
    getExposureScoreColorClass(scoreName) {
      switch (scoreName.toLowerCase()) {
        case 'critical':
          return 'text-danger'
        case 'high':
          return 'text-warning'
        case 'medium':
          return 'text-info'
        case 'low':
          return 'text-success'
        case 'info':
          return 'text-secondary'
        default:
          return 'text-secondary'
      }
    },
    downloadPdf () {
      const element = document.getElementById('pdfcontent')
      html2pdf(element, this.pdfConfig)
    },

    /**
     * Export table data to CSV format
     * This method creates a CSV file containing all filtered asset data
     * with proper formatting for multi-value fields
     */
    exportTableToCSV () {
      // Build headers from columns
      const headers = this.columns.map(col => col.label).join(',')

      // Transform data rows
      const rows = this.filteredAssets.map(asset => {
        // Handle netbios_name (can be array or string)
        let netbiosName = ''
        if (Array.isArray(asset.netbios_name)) {
          netbiosName = `"${asset.netbios_name.join('; ')}"`
        } else if (typeof asset.netbios_name === 'string') {
          netbiosName = `"${asset.netbios_name}"`
        }

        // Handle fqdn (can be array or string)
        let fqdn = ''
        if (Array.isArray(asset.fqdn)) {
          fqdn = `"${asset.fqdn.join('; ')}"`
        } else if (typeof asset.fqdn === 'string') {
          fqdn = `"${asset.fqdn}"`
        }

        // Handle ipv4 (can be array or string)
        let ipv4 = ''
        if (Array.isArray(asset.ipv4)) {
          ipv4 = `"${asset.ipv4.join('; ')}"`
        } else if (typeof asset.ipv4 === 'string') {
          ipv4 = `"${asset.ipv4}"`
        }

        // Get labels for the asset
        let labels = ''
        if (Array.isArray(asset.ipv4)) {
          const allLabels = asset.ipv4.flatMap(ip => this.labelsFromTargetGroups(ip))
          labels = `"${[...new Set(allLabels)].join('; ')}"`
        } else if (typeof asset.ipv4 === 'string') {
          labels = `"${this.labelsFromTargetGroups(asset.ipv4).join('; ')}"`
        }

        // Handle operating_system (can be array or string)
        let operatingSystem = ''
        if (Array.isArray(asset.operating_system)) {
          operatingSystem = `"${asset.operating_system.join('; ')}"`
        } else if (typeof asset.operating_system === 'string') {
          operatingSystem = `"${asset.operating_system}"`
        }

        return [
          netbiosName,
          fqdn,
          ipv4,
          labels,
          asset.last_seen || '',
          operatingSystem,
          asset.exposure_score !== undefined ? asset.exposure_score : '0'
        ].join(',')
      })

      // Combine headers and rows
      const csv = [headers, ...rows].join('\n')

      // Download the CSV file with date range in filename
      const dateRangeText = this.getSelectedDateRangeText().toLowerCase().replace(/\s+/g, '_')
      this.downloadCSV(csv, `assets_${dateRangeText}.csv`)
    },

    /**
     * Helper function to download CSV data as a file
     * Creates a downloadable blob and triggers the browser download
     */
    downloadCSV (csv, filename) {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (navigator.msSaveBlob) {
        // IE 10+
        navigator.msSaveBlob(blob, filename)
      } else {
        // Other browsers
        const url = URL.createObjectURL(blob)
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },

    /**
     * Filter assets by date range using last_seen field (client-side filtering)
     * Since the Tenable /assets endpoint doesn't support server-side date filtering,
     * we need to filter the results on the client side
     *
     * @param {Array} assets - Array of asset objects to filter
     * @returns {Array} - Filtered array of assets
     */
    filterAssetsByDateRange (assets) {
      if (!assets || !Array.isArray(assets)) {
        console.log('No assets to filter or invalid input:', assets)
        return []
      }

      // Calculate cutoff date based on selected date range
      const now = new Date()
      const cutoffDate = new Date()
      cutoffDate.setDate(now.getDate() - parseInt(this.selectedDateRange))

      console.log('Filtering assets by date range:', this.selectedDateRange, 'days')
      console.log('Cutoff date:', cutoffDate.toISOString())
      console.log('Assets to filter:', assets.length)

      const filteredAssets = assets.filter(asset => {
        if (!asset.last_seen) {
          // If no last_seen date, exclude from results
          return false
        }

        try {
          // Handle different date formats from Tenable API
          let lastSeenDate

          if (typeof asset.last_seen === 'number') {
            // Unix timestamp (seconds or milliseconds)
            if (asset.last_seen > 1000000000000) {
              // Milliseconds
              lastSeenDate = new Date(asset.last_seen)
            } else {
              // Seconds
              lastSeenDate = new Date(asset.last_seen * 1000)
            }
          } else if (typeof asset.last_seen === 'string') {
            // ISO string or other date format
            lastSeenDate = new Date(asset.last_seen)
          } else {
            // Unknown format
            console.warn('Unknown last_seen format for asset:', asset.last_seen)
            return false
          }

          // Check if date parsing was successful
          if (isNaN(lastSeenDate.getTime())) {
            console.warn('Invalid last_seen date for asset:', asset.last_seen)
            return false
          }

          // Include asset if last_seen is within the selected date range
          const isWithinRange = lastSeenDate >= cutoffDate

          if (this.selectedDateRange === '1') {
            // For debugging 1-day filter
            console.log(`Asset ${asset.netbios_name || asset.ipv4}: last_seen=${lastSeenDate.toISOString()}, cutoff=${cutoffDate.toISOString()}, included=${isWithinRange}`)
          }

          return isWithinRange
        } catch (error) {
          console.error('Error parsing last_seen date for asset:', asset, error)
          return false
        }
      })

      console.log('Assets after date filtering:', filteredAssets.length, 'out of', assets.length)
      return filteredAssets
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="container" style="max-width: 1420px !important;">
      <div class="row page-title align-items-center" />
      <div class="col-12">
        <b-button
          class="btn btn-primary btn-sm mr-1"
          variant="light"
          @click="downloadPdf"
        >
          Download PDF
        </b-button>
        <b-button
          class="btn btn-primary btn-sm mr-1"
          variant="light"
          @click="exportTableToCSV"
        >
          <i class="mdi mdi-download mr-1" />Export CSV
        </b-button>
        <br><br>
        <div
          id="pdfcontent"
          style="width:1280px; break-after: page; clear: both;padding-left:20px;padding-right:20px;"
        >
          <section slot="pdf-content">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <h4 class="header-title">
                    Assets with 
                    <span v-if="selectedExposureScores.length === 0">All</span>
                    <span v-else-if="selectedExposureScores.length === exposureScoreOptions.length">All</span>
                    <span v-else>
                      {{ selectedExposureScores.join(', ') }}
                    </span>
                    Exposure Scores - Last {{ getSelectedDateRangeText() }}
                  </h4>
                  <span>Updated: {{ updated }}</span>
                </div>
                
                <!-- Search and filter controls -->
                <!-- Search and filter controls -->
                <div class="row mb-2">
                  <div class="col-md-2">
                    <div class="form-group">
                      <label class="form-label">Search Assets</label>
                      <input
                        v-model="filters.asset"
                        type="text"
                        class="form-control"
                        placeholder="Search by name, FQDN, IP or OS"
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="form-label">Date Range</label>
                      <select
                        v-model="selectedDateRange"
                        class="form-control"
                      >
                        <option
                          v-for="option in dateRangeOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.text }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label class="form-label">Labels ({{ allLabels.length }})</label>
                      <div style="border: 1px solid #ccc; height: 70px; overflow-y: auto; padding: 5px;">
                        <div
                          v-for="label in allLabels"
                          :key="label"
                          class="form-check"
                        >
                          <input
                            :id="`label-${label}`"
                            v-model="selectedLabels"
                            type="checkbox"
                            :value="label"
                            class="form-check-input"
                          >
                          <label
                            :for="`label-${label}`"
                            class="form-check-label"
                          >
                            {{ label }}
                          </label>
                        </div>
                      </div>
                      <small
                        v-if="allLabels.length === 0"
                        class="text-muted"
                      >
                        No labels available
                      </small>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label mb-2">Filter by Exposure Score</label>
                      <div class="exposure-checkboxes d-flex flex-nowrap align-items-center">
                        <div
                          v-for="option in exposureScoreOptions"
                          :key="option.value"
                          class="form-check form-check-inline me-2"
                        >
                          <input
                            :id="`exposure-${option.value}`"
                            v-model="selectedExposureScores"
                            type="checkbox"
                            :value="option.value"
                            class="form-check-input"
                          >
                          <label
                            :for="`exposure-${option.value}`"
                            class="form-check-label"
                            :class="getExposureScoreColorClass(option.value)"
                          >
                            {{ option.text }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Loading message -->
                <div v-if="loading" class="text-center py-5">
                  <p class="mt-2">Loading assets...</p>
                </div>
                
                <!-- Chart section for OS families -->
                <div v-else-if="filteredAssets.length > 0" class="mb-5">
                  <h5 class="mb-3">Top Operating System Families by Average Exposure Score</h5>
                  <div class="chart-container">
                    <apexchart
                      height="350"
                      :options="chartOptions"
                      :series="chartSeries"
                    ></apexchart>
                  </div>
                </div>
                
                <!-- Chart section for exact OS versions -->
                <div v-if="!loading && filteredAssets.length > 0" class="mb-5">
                  <h5 class="mb-3">Top Exact Operating Systems by Total Exposure Score</h5>
                  <div class="chart-container">
                    <apexchart
                      height="350"
                      :options="exactOSChartOptions"
                      :series="exactOSChartSeries"
                    ></apexchart>
                  </div>
                </div>
                
                <!-- Chart section for maximum exposure score by OS -->
                <div v-if="!loading && filteredAssets.length > 0" class="mb-5">
                  <h5 class="mb-3">Top Operating Systems by Maximum Exposure Score</h5>
                  <div class="chart-container">
                    <apexchart
                      height="600"
                      :options="maxScoreChartOptions"
                      :series="maxScoreChartSeries"
                    ></apexchart>
                  </div>
                </div>

                <!-- Table content (only shown when not loading) -->
                <div v-if="!loading" class="table-responsive">
                  <!-- Debug info - can be removed after fixing -->
                  <!-- Debug info - can be removed after fixing -->
                  <div v-if="assets.length === 0" class="alert alert-info mb-3">
                    No asset seen. Please check your filters.
                  </div>
                  
                  <div v-else-if="filteredAssets.length === 0" class="alert alert-warning mb-3">
                    No assets match your search criteria. Try adjusting your filters.
                  </div>
                  
                  <table class="table table-centered table-hover mb-0">
                    <thead>
                      <tr>
                        <th 
                          v-for="column in columns" 
                          :key="column.key"
                          :class="{ 'sortable': column.sortable, 'active': sortKey === column.key }"
                          @click="column.sortable && toggleSort(column.key)"
                        >
                          {{ column.label }}
                          <i v-if="column.sortable" :class="getSortIcon(column.key)" class="sort-icon"></i>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(asset, index) in filteredAssets" :key="index">
                        <td>
                          <template v-if="Array.isArray(asset.netbios_name) && asset.netbios_name.length > 0">
                            <ul class="list-unstyled mb-0">
                              <li v-for="(name, nameIndex) in asset.netbios_name" :key="nameIndex">
                                <code>{{ name }}</code>
                              </li>
                            </ul>
                          </template>
                          <template v-else-if="typeof asset.netbios_name === 'string' && asset.netbios_name.includes(',')">
                            <ul class="list-unstyled mb-0">
                              <li v-for="(name, nameIndex) in asset.netbios_name.split(',')" :key="nameIndex">
                                <code>{{ name.trim() }}</code>
                              </li>
                            </ul>
                          </template>
                          <template v-else-if="asset.netbios_name && asset.netbios_name.length === 1">
                            <code>{{ asset.netbios_name }}</code>
                          </template>
                          <template v-else>
                            <code>N/A</code>
                          </template>
                        </td>
                        <td>
                          <template v-if="Array.isArray(asset.fqdn) && asset.fqdn.length > 0">
                            <ul class="list-unstyled mb-0">
                              <li v-for="(fqdn, fqdnIndex) in asset.fqdn" :key="fqdnIndex">
                                <strong>{{ fqdn }}</strong>
                              </li>
                            </ul>
                          </template>
                          <template v-else-if="typeof asset.fqdn === 'string' && asset.fqdn.includes(',')">
                            <ul class="list-unstyled mb-0">
                              <li v-for="(fqdn, fqdnIndex) in asset.fqdn.split(',')" :key="fqdnIndex">
                                <strong>{{ fqdn.trim() }}</strong>
                              </li>
                            </ul>
                          </template>
                          <template v-else-if="asset.fqdn && asset.fqdn.length === 1">
                            <strong>{{ asset.fqdn }}</strong>
                          </template>
                          <template v-else>
                            <strong>N/A</strong>
                          </template>
                        </td>
                        <td>
                          <template v-if="Array.isArray(asset.ipv4) && asset.ipv4.length > 0">
                            <ul class="list-unstyled mb-0">
                              <li v-for="(ip, ipIndex) in asset.ipv4" :key="ipIndex">
                                <code>{{ ip }}</code>
                              </li>
                            </ul>
                          </template>
                          <template v-else-if="typeof asset.ipv4 === 'string' && asset.ipv4.includes(',')">
                            <ul class="list-unstyled mb-0">
                              <li v-for="(ip, ipIndex) in asset.ipv4.split(',')" :key="ipIndex">
                                <code>{{ ip.trim() }}</code>
                              </li>
                            </ul>
                          </template>
                          <template v-else-if="asset.ipv4 && asset.ipv4.length === 1">
                            <code>{{ asset.ipv4 }}</code>
                          </template>
                          <template v-else>
                            <code>N/A</code>
                          </template>
                        </td>
                        <td>
                          <ul class="list-unstyled mb-0">
                            <template v-if="Array.isArray(asset.ipv4)">
                              <template v-for="ip in asset.ipv4">
                                <li
                                  v-for="label in labelsFromTargetGroups(ip)"
                                  :key="label"
                                >
                                  {{ label }}
                                </li>
                              </template>
                            </template>
                            <template v-else-if="typeof asset.ipv4 === 'string'">
                              <li
                                v-for="label in labelsFromTargetGroups(asset.ipv4)"
                                :key="label"
                              >
                                {{ label }}
                              </li>
                            </template>
                          </ul>
                        </td>
                        <td>{{ formatDate(asset.last_seen) }}</td>
                        <td>
                          <template v-if="Array.isArray(asset.operating_system) && asset.operating_system.length > 0">
                            <ul class="list-unstyled mb-0">
                              <li v-for="(os, osIndex) in asset.operating_system" :key="osIndex">
                                {{ os }}
                              </li>
                            </ul>
                          </template>
                          <template v-else-if="typeof asset.operating_system === 'string' && asset.operating_system.includes(',')">
                            <ul class="list-unstyled mb-0">
                              <li v-for="(os, osIndex) in asset.operating_system.split(',')" :key="osIndex">
                                {{ os.trim() }}
                              </li>
                            </ul>
                          </template>
                          <template v-else-if="asset.operating_system && asset.operating_system.length === 1">
                            {{ asset.operating_system }}
                          </template>
                          <template v-else>
                            N/A
                          </template>
                        </td>
                        <td>
                          <span v-if="asset.exposure_score !== undefined && asset.exposure_score !== null" :class="getExposureScoreClass(asset.exposure_score)">
                            {{ asset.exposure_score }}
                          </span>
                          <span v-else class="badge bg-secondary">0</span>
                        </td>
                      </tr>
                      <tr v-if="filteredAssets.length === 0">
                        <td
                          :colspan="columns.length"
                          class="text-center"
                        >
                          No assets found
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.sortable {
  cursor: pointer;
  position: relative;
}

.sortable.active {
  background-color: rgba(0, 0, 0, 0.05);
}

.sort-icon {
  font-size: 0.8rem;
  margin-left: 5px;
}
.table th {
  font-weight: 600;
}

.table td {
  vertical-align: middle;
  padding: 0.75rem 0.5rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.list-unstyled {
  padding-left: 0;
  list-style: none;
}

.list-unstyled li {
  margin-bottom: 0.25rem;
}

.list-unstyled li:last-child {
  margin-bottom: 0;
}

code {
  padding: 0.2rem 0.4rem;
  font-size: 0.875em;
  color: #212529;
  background-color: #f8f9fa;
  border-radius: 0.2rem;
}

</style> 